import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { PageHeader, BrutalCard, EmptyState, Chip, StatCard } from "../components/brutal";
import { Link } from "react-router-dom";
import { Coins, CheckCircle2, Clock, XCircle, ArrowRight, Sparkles, Mail, Phone } from "lucide-react";

const STATUS_COLOR = {
  active: "bg-brand-blue text-white",
  rejected: "bg-red-500 text-white",
  completed: "bg-brand-green",
};

const daysSince = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!t) return null;
  return Math.floor((Date.now() - t) / 86_400_000);
};

const fmtDays = (n) => {
  if (n === null) return "—";
  if (n === 0) return "today";
  if (n === 1) return "yesterday";
  if (n < 7) return `${n}d ago`;
  if (n < 30) return `${Math.floor(n / 7)}w ago`;
  return `${Math.floor(n / 30)}mo ago`;
};

export default function MyReferralsPage() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/referrals/me").then((r) => setItems(r.data));
  }, []);

  const summary = useMemo(() => {
    if (!items) return { hires: 0, active: 0, rejected: 0, earned: 0, pending: 0 };
    let hires = 0, active = 0, rejected = 0, earned = 0, pending = 0;
    items.forEach((r) => {
      earned += r.points_earned || 0;
      if (r.status === "rejected") rejected += 1;
      else if (r.is_hired) hires += 1;
      else {
        active += 1;
        pending += r.next_milestone?.points || 0;
      }
    });
    return { hires, active, rejected, earned, pending };
  }, [items]);

  if (!items) return <div className="p-10 text-center">Loading…</div>;

  if (items.length === 0) {
    return (
      <div>
        <PageHeader title="My Referrals" subtitle="Track every candidate you've submitted." />
        <EmptyState
          title="No referrals yet"
          description="Submit your first candidate and start earning points."
          action={<Link to="/app/refer" className="btn-brutal btn-primary" data-testid="empty-refer-btn">Refer now</Link>}
        />
      </div>
    );
  }

  const active = items.filter((r) => r.status !== "rejected" && !r.is_hired);
  const hired = items.filter((r) => r.is_hired);
  const rejected = items.filter((r) => r.status === "rejected");

  return (
    <div>
      <PageHeader
        title="My Referrals"
        subtitle="Track every candidate you've submitted."
        right={
          <Link to="/app/refer" className="btn-brutal btn-primary" data-testid="my-refs-cta">
            Refer another <ArrowRight className="w-4 h-4" />
          </Link>
        }
      />

      <div className="bento-grid mb-6">
        <StatCard testid="my-refs-banked" label="Banked points" value={summary.earned.toLocaleString()} sub={`= €${summary.earned.toLocaleString()}`} color="bg-brand-yellow" icon={<Coins className="w-6 h-6" />} />
        <StatCard testid="my-refs-pending" label="Potential more" value={summary.pending.toLocaleString()} sub={summary.active === 0 ? "No active referrals" : `If ${summary.active} active hit the next stage`} color="bg-white" icon={<Sparkles className="w-6 h-6" />} />
        <StatCard testid="my-refs-hires" label="Hires landed" value={summary.hires} color="bg-brand-green" icon={<CheckCircle2 className="w-6 h-6" />} />
        <StatCard testid="my-refs-rejected" label="Didn't make it" value={summary.rejected} color="bg-white" icon={<XCircle className="w-6 h-6" />} />
      </div>

      <Section title="Active" subtitle="In the funnel — points await." testid="section-active" items={active} accent="bg-brand-blue/10" />
      <Section title="Hires" subtitle="The wins — keep them coming." testid="section-hired" items={hired} accent="bg-brand-green/40" />
      <Section title="Didn't make it" subtitle="No points awarded — onwards." testid="section-rejected" items={rejected} accent="bg-neutral-100" muted />
    </div>
  );
}

function Section({ title, subtitle, items, testid, accent = "bg-white", muted = false }) {
  if (items.length === 0) return null;
  return (
    <section className="mb-7" data-testid={testid}>
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h2 className="font-display font-black text-2xl leading-none">{title}</h2>
          <p className="text-xs text-neutral-600 mt-1">{subtitle}</p>
        </div>
        <span className="chip bg-white text-xs">{items.length}</span>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {items.map((r) => (
          <ReferralCard key={r.id} r={r} accent={accent} muted={muted} />
        ))}
      </div>
    </section>
  );
}

function ReferralCard({ r, accent, muted }) {
  const idle = daysSince(r.updated_at || r.created_at);
  const isStale = !muted && !r.is_hired && idle !== null && idle >= 14;
  return (
    <BrutalCard className={`p-4 ${accent} ${muted ? "opacity-70" : ""}`} data-testid={`referral-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-display font-black text-lg leading-tight truncate" data-testid={`referral-name-${r.id}`}>{r.candidate_name}</div>
          <div className="text-xs text-neutral-700 truncate font-semibold mt-0.5">{r.job_title}</div>
          <div className="flex flex-col gap-0.5 text-[11px] text-neutral-600 mt-2">
            {r.candidate_email && (
              <span className="inline-flex items-center gap-1 truncate"><Mail className="w-3 h-3 flex-shrink-0" /> {r.candidate_email}</span>
            )}
            {r.candidate_phone && (
              <span className="inline-flex items-center gap-1 truncate"><Phone className="w-3 h-3 flex-shrink-0" /> {r.candidate_phone}</span>
            )}
          </div>
        </div>
        <span className={`chip ${STATUS_COLOR[r.status] || "bg-white"} text-[10px] flex-shrink-0`}>{r.status}</span>
      </div>

      <div className="border-t-2 border-black/10 mt-3 pt-3 flex items-center justify-between gap-3 flex-wrap">
        <Chip color={r.is_hired ? "bg-brand-green" : "bg-brand-yellow"}>{r.current_stage_name || "—"}</Chip>
        <div className="flex items-center gap-2 text-[11px]">
          <span className={`inline-flex items-center gap-1 font-bold ${isStale ? "text-red-700" : "text-neutral-600"}`} data-testid={`referral-idle-${r.id}`}>
            <Clock className="w-3 h-3" /> {fmtDays(idle)}{isStale ? " · idle" : ""}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap mt-3">
        <div className="flex items-center gap-1 text-xs">
          <Coins className="w-3.5 h-3.5 text-brand-orange" />
          <span className="font-display font-black text-base" data-testid={`referral-earned-${r.id}`}>{(r.points_earned || 0).toLocaleString()}</span>
          <span className="text-neutral-600">earned</span>
        </div>
        {r.next_milestone && (
          <div className="text-[11px] text-neutral-700 inline-flex items-center gap-1" data-testid={`referral-next-${r.id}`}>
            Next: <strong>{r.next_milestone.name}</strong>
            <span className="chip bg-white text-[10px] py-0">+{r.next_milestone.points}</span>
          </div>
        )}
      </div>
    </BrutalCard>
  );
}
